// @catalog F.030
// PackSizeEditor — Bloque controlado del input "Cant. compra (usuario)"
// dentro de DecisionCard. Maneja el toggle readonly/editing, los steppers,
// los botones Editar / Cancelar / Aplicar / Restablecer y el tooltip
// explicativo. Extraído de DecisionCard para cumplir P7 (<=500 líneas).
//
// Diseño compacto (2026-05-21 iteración):
//   - Modo readonly = 1 sola fila: input pequeño + ícono lápiz (+ ícono
//     restablecer si hay override usuario). Tamaño acorde al resto de
//     stats del Decision Card (no domina visualmente).
//   - Modo editing = 1 fila input + steppers, segunda fila Cancelar/Aplicar.
//   - Tooltip wrapping todo el grupo explicando qué hace el pack size.

import { useEffect, useState, type ChangeEvent } from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const PACK_TOOLTIP_TEXT =
  "Cantidad por paquete de compra. El sistema redondea las cantidades sugeridas a múltiplos de este valor. Ej: si el forecast sugiere 10 piezas y el paquete es 4, se comprarán 12 (3 paquetes).";

interface PackSizeEditorProps {
  /** Valor efectivo (override usuario ?? auto ?? 1) — se muestra en readonly. */
  effectiveCant: number;
  /** True si hay override del usuario (purchaseCantUser != null). */
  hasUserOverride: boolean;
  /** Input controlled value (vive en el padre DecisionCard). */
  purchaseQty: string;
  setPurchaseQty: (v: string) => void;
  saving: boolean;
  saved: boolean;
  onApply: () => void;
  onReset?: () => void;
}

const numericTextSx = {
  fontFamily: "var(--font-num)",
  fontVariantNumeric: "tabular-nums" as const,
  fontSize: 13,
  fontWeight: 600,
  textAlign: "right" as const,
};

const iconBtnSx = {
  width: 26,
  height: 26,
  bgcolor: "transparent",
  color: "var(--ink-3)",
  display: "grid",
  placeItems: "center",
  borderRadius: 0.75,
  border: 0,
  font: "inherit",
  cursor: "pointer",
  flexShrink: 0,
  "&:hover:not(:disabled)": {
    bgcolor: "var(--bg-2)",
    color: "var(--ink-1)",
  },
  "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
} as const;

const stepperSx = {
  width: 22,
  bgcolor: "var(--bg)",
  color: "var(--ink-2)",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  fontSize: 13,
  font: "inherit",
  border: 0,
  "&:hover:not(:disabled)": {
    bgcolor: "var(--bg-2)",
    color: "var(--ink-1)",
  },
  "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
} as const;

export function PackSizeEditor({
  effectiveCant,
  hasUserOverride,
  purchaseQty,
  setPurchaseQty,
  saving,
  saved,
  onApply,
  onReset,
}: PackSizeEditorProps) {
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (saved) setEditing(false);
  }, [saved]);

  const inputNum = parseInt(purchaseQty, 10);
  const hasChanges =
    editing &&
    !Number.isNaN(inputNum) &&
    inputNum >= 1 &&
    inputNum !== effectiveCant;

  const handleStep = (delta: number) => {
    const cur = Number.isNaN(inputNum) ? effectiveCant : inputNum;
    const next = Math.max(1, Math.min(10000, cur + delta));
    setPurchaseQty(String(next));
  };

  const handleStartEdit = () => {
    setPurchaseQty(String(effectiveCant));
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setPurchaseQty(String(effectiveCant));
    setEditing(false);
  };

  // ── Modo readonly: 1 sola fila compacta ──────────────────────────
  if (!editing) {
    return (
      <Tooltip title={PACK_TOOLTIP_TEXT} arrow placement="left">
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ minWidth: 100 }}
        >
          <Box
            sx={{
              ...numericTextSx,
              flex: 1,
              minWidth: 0,
              border: "1px solid var(--border)",
              borderRadius: 0.75,
              bgcolor: "var(--bg-2)",
              px: 1,
              py: 0.5,
              color: hasUserOverride ? "var(--accent)" : "var(--ink-1)",
              cursor: "default",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              height: 28,
            }}
          >
            {effectiveCant}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleStartEdit}
            disabled={saving}
            aria-label="Editar pack size"
            sx={iconBtnSx}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
          {hasUserOverride && onReset && (
            <Tooltip
              title="Restablecer al valor auto detectado"
              arrow
              placement="left"
            >
              <Box
                component="button"
                type="button"
                onClick={onReset}
                disabled={saving}
                aria-label="Restablecer al auto detectado"
                sx={iconBtnSx}
              >
                <RestartAltIcon sx={{ fontSize: 14 }} />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Tooltip>
    );
  }

  // ── Modo editing: 2 filas compactas ──────────────────────────────
  return (
    <Stack spacing={0.5} sx={{ minWidth: 140 }}>
      <Tooltip title={PACK_TOOLTIP_TEXT} arrow placement="left">
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            border: "1px solid var(--accent)",
            borderRadius: 0.75,
            bgcolor: "var(--surface)",
            overflow: "hidden",
            height: 28,
            "&:focus-within": {
              boxShadow: "0 0 0 2px var(--accent-soft)",
            },
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => handleStep(-1)}
            disabled={saving}
            aria-label="Restar"
            sx={{ ...stepperSx, borderRight: "1px solid var(--border)" }}
          >
            −
          </Box>
          <Box
            component="input"
            type="number"
            min={1}
            max={10000}
            value={purchaseQty}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPurchaseQty(e.target.value)
            }
            disabled={saving}
            autoFocus
            aria-label="Cantidad por paquete (override usuario)"
            sx={{
              ...numericTextSx,
              flex: 1,
              minWidth: 0,
              border: 0,
              outline: 0,
              bgcolor: "transparent",
              px: 0.75,
              color: "var(--ink-1)",
              "&:disabled": { color: "var(--ink-3)" },
            }}
          />
          <Box
            component="button"
            type="button"
            onClick={() => handleStep(1)}
            disabled={saving}
            aria-label="Sumar"
            sx={{ ...stepperSx, borderLeft: "1px solid var(--border)" }}
          >
            +
          </Box>
        </Box>
      </Tooltip>

      <Stack direction="row" spacing={0.5}>
        <Box
          component="button"
          type="button"
          onClick={handleCancelEdit}
          disabled={saving}
          sx={{
            flex: "0 0 auto",
            bgcolor: "transparent",
            color: "var(--ink-3)",
            px: 1,
            py: 0.5,
            fontSize: 11.5,
            fontWeight: 500,
            borderRadius: 0.75,
            border: "1px solid var(--border)",
            font: "inherit",
            cursor: "pointer",
            "&:hover:not(:disabled)": {
              bgcolor: "var(--bg-2)",
              color: "var(--ink-1)",
            },
            "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
          }}
        >
          Cancelar
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onApply}
          disabled={!hasChanges || saving}
          sx={{
            flex: 1,
            bgcolor: saved
              ? "var(--pos)"
              : !hasChanges
                ? "var(--bg-2)"
                : "var(--ink-1)",
            color: saved || hasChanges ? "#fff" : "var(--ink-3)",
            px: 1,
            py: 0.5,
            fontSize: 11.5,
            fontWeight: 600,
            borderRadius: 0.75,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            border: 0,
            font: "inherit",
            cursor: hasChanges && !saving ? "pointer" : "not-allowed",
            transition: "background 0.15s",
            "&:hover:not(:disabled)": {
              bgcolor: saved ? "var(--pos)" : "#1a2236",
            },
            "&:focus-visible": {
              outline: "2px solid var(--accent)",
              outlineOffset: 2,
            },
          }}
        >
          {saved ? (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 12 }} />
              Guardado
            </>
          ) : saving ? (
            "Guardando…"
          ) : (
            "Aplicar"
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
